'use client'

import Link from 'next/link'
import { ArrowRight, MessageSquare } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/AuthContext'
import type { NotificationItem, ProtocolMovedContext } from '@/services/notifications/types'
import { Notification } from '../Notification'
import { getKindStyle } from '../kind-style'
import { defaultRenderer } from './default'
import type { NotificationRenderer, NotificationViewProps } from './types'

const TRANSFER_LABELS: Record<ProtocolMovedContext['kind'], string> = {
  takeover: 'Atendimento assumido',
  transfer: 'Atendimento transferido',
  auto: 'Transferência automática',
}

const CHANNEL_FALLBACK = 'Canal removido'

/**
 * O `context` é `dict[str, Any]` no back — nada garante a forma em runtime.
 * Sem os campos essenciais, o renderer devolve `null` e quem chama cai no
 * default (que só mostra title/body, sempre presentes).
 */
function readContext(notification: NotificationItem): ProtocolMovedContext | null {
  const ctx = notification.context as Partial<ProtocolMovedContext> | undefined
  if (!ctx?.protocol_id || !ctx.to_channel_id || !ctx.contact_name) return null
  return {
    kind: ctx.kind ?? 'transfer',
    protocol_id: ctx.protocol_id,
    contact_name: ctx.contact_name,
    from_channel_id: ctx.from_channel_id ?? '',
    from_channel_name: ctx.from_channel_name ?? null,
    to_channel_id: ctx.to_channel_id,
    to_channel_name: ctx.to_channel_name ?? null,
    actor_user_id: ctx.actor_user_id ?? '',
    actor_username: ctx.actor_username ?? '',
  }
}

function ChannelHop({ ctx, className }: { ctx: ProtocolMovedContext; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs', className)}>
      <span className="rounded bg-muted px-1.5 py-0.5 font-medium">
        {ctx.from_channel_name ?? CHANNEL_FALLBACK}
      </span>
      <ArrowRight className="size-3 shrink-0 text-muted-foreground" />
      <span className="rounded bg-muted px-1.5 py-0.5 font-medium">
        {ctx.to_channel_name ?? CHANNEL_FALLBACK}
      </span>
    </span>
  )
}

function PreviewCard(props: NotificationViewProps) {
  const { notification, href, showReadAction = true, showDeleteAction = true } = props
  const ctx = readContext(notification)
  if (!ctx) return <defaultRenderer.Preview {...props} />

  const style = getKindStyle(notification.kind)

  const card = (
    <div
      className={cn(
        'w-full min-w-0 overflow-hidden rounded-lg border p-3 transition-all duration-150 hover:shadow-md',
        style.borderClass,
        style.bgClass,
        href && 'cursor-pointer hover:bg-accent/40',
      )}
    >
      <Notification notification={notification}>
        <div className="flex items-start gap-3">
          <Notification.KindIcon className="mt-0.5" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex items-start justify-between gap-2">
              <p className="min-w-0 flex-1 text-sm font-medium leading-tight">
                {TRANSFER_LABELS[ctx.kind]} ·{' '}
                <span className="break-words">{ctx.contact_name}</span>
              </p>
              <Notification.KindBadge />
            </div>
            <ChannelHop ctx={ctx} />
            <div className="flex items-center justify-between gap-2">
              <Notification.Time className="text-[11px]" />
              {(showReadAction || showDeleteAction) && (
                <div className="flex gap-1">
                  {showReadAction && <Notification.ReadAction onSuccess={props.onMarkedRead} />}
                  {showDeleteAction && <Notification.DeleteAction onSuccess={props.onDeleted} />}
                </div>
              )}
            </div>
            {ctx.actor_username && (
              <p className="text-[11px] text-muted-foreground">por {ctx.actor_username}</p>
            )}
          </div>
        </div>
      </Notification>
    </div>
  )

  return href ? (
    <Link href={href} className="block">
      {card}
    </Link>
  ) : (
    card
  )
}

function FullView(props: NotificationViewProps) {
  const { notification, showReadAction = true, showDeleteAction = true } = props
  const { user } = useAuth()
  const ctx = readContext(notification)
  if (!ctx) return <defaultRenderer.Full {...props} />

  // Quem recebe o aviso quase sempre PERDEU o chat (foi expulso do canal de
  // origem). Só oferece o link quando o canal de destino está no escopo do
  // usuário — link que dá 403 é pior que link nenhum.
  const hasDestinationAccess = (user?.channels ?? []).some((c) => c.db_id === ctx.to_channel_id)

  return (
    <Notification notification={notification}>
      <article className="space-y-6">
        <header className="space-y-2">
          <div className="flex items-center gap-2">
            <Notification.KindIcon className="size-5" />
            <Notification.KindBadge />
          </div>
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
            {TRANSFER_LABELS[ctx.kind]}
          </h1>
          <Notification.Time absolute className="block text-sm text-muted-foreground" />
        </header>

        <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Contato:</span>
            <span className="text-sm font-medium break-words">{ctx.contact_name}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Canal:</span>
            <ChannelHop ctx={ctx} className="text-sm" />
          </div>
          {ctx.actor_username && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">Por:</span>
              <span className="text-sm font-medium">{ctx.actor_username}</span>
            </div>
          )}
        </div>

        <Notification.Body className="text-base leading-relaxed" />

        {hasDestinationAccess ? (
          <Link
            href={`/chatbot/${ctx.to_channel_id}/${ctx.protocol_id}`}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent"
          >
            <MessageSquare className="size-4" />
            Abrir conversa em {ctx.to_channel_name ?? CHANNEL_FALLBACK}
          </Link>
        ) : (
          <p className="text-sm text-muted-foreground">
            Você não tem acesso ao canal de destino — a conversa não fica mais visível pra você.
          </p>
        )}

        <Notification.Meta />

        {(showReadAction || showDeleteAction) && (
          <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-4">
            {showDeleteAction && (
              <Notification.DeleteAction variant="labeled" onSuccess={props.onDeleted} />
            )}
            {showReadAction && (
              <Notification.ReadAction variant="labeled" onSuccess={props.onMarkedRead} />
            )}
          </div>
        )}
      </article>
    </Notification>
  )
}

export const protocolMovedRenderer: NotificationRenderer = {
  Preview: PreviewCard,
  Full: FullView,
}
